/**
 * Boomfection Landing — Achievement Popup Module
 * Listens for achievement:unlocked socket events and shows animated popups
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

import { t } from '/js/i18n.js';

/** @type {HTMLElement|null} Popup container */
let popupContainer = null;

/** Achievement icon mapping */
const ICON_MAP = {
    trophy: '\uD83C\uDFC6',
    crown: '\uD83D\uDC51',
    star: '\u2B50',
    gem: '\uD83D\uDC8E',
    fire: '\uD83D\uDD25',
    lightning: '\u26A1',
    skull: '\uD83D\uDC80',
    play: '\u25B6\uFE0F',
    controller: '\uD83C\uDFAE',
    medal: '\uD83C\uDFC5',
    virus: '\uD83E\uDDA0',
    explosion: '\uD83D\uDCA5',
    bomb: '\uD83D\uDCA3',
    nuke: '\u2622\uFE0F',
    chain: '\uD83D\uDD17',
    domino: '\uD83C\uDFB2',
    crosshair: '\uD83C\uDFAF',
    sword: '\u2694\uFE0F',
    scythe: '\uD83D\uDDE1\uFE0F',
    shield: '\uD83D\uDEE1\uFE0F',
    phoenix: '\uD83D\uDD25',
    clock: '\u23F1\uFE0F',
    globe: '\uD83C\uDF0D',
    users: '\uD83D\uDC65',
    home: '\uD83C\uDFE0'
};

/**
 * Ensures the popup container exists in the DOM
 * @returns {HTMLElement}
 */
function getPopupContainer() {
    if (popupContainer && document.body.contains(popupContainer)) {
        return popupContainer;
    }
    popupContainer = document.createElement('div');
    popupContainer.id = 'achievement-popup-container';
    popupContainer.style.cssText =
        'position:fixed;top:20px;right:20px;z-index:10000;display:flex;flex-direction:column;gap:10px;pointer-events:none;';
    document.body.appendChild(popupContainer);
    return popupContainer;
}

/**
 * Shows an achievement popup with slide-in animation
 * @param {Object} data - Achievement data
 * @param {string} data.code - Achievement code
 * @param {string} data.name - Achievement display name
 * @param {string} data.description - Achievement description
 * @param {string} data.icon - Icon key
 * @param {number} [data.points] - Achievement points
 * @param {Object} [soundManager] - Optional sound manager instance
 */
function showAchievementPopup(data, soundManager) {
    const container = getPopupContainer();

    const popup = document.createElement('div');
    popup.className = 'achievement-popup';

    const iconEl = document.createElement('div');
    iconEl.className = 'achievement-popup-icon';
    iconEl.textContent = ICON_MAP[data.icon] || ICON_MAP.trophy;

    const contentEl = document.createElement('div');
    contentEl.className = 'achievement-popup-content';

    const titleEl = document.createElement('div');
    titleEl.className = 'achievement-popup-title';
    titleEl.textContent = t('achievement.unlocked');

    const nameEl = document.createElement('div');
    nameEl.className = 'achievement-popup-name';
    nameEl.textContent = data.name;

    const descEl = document.createElement('div');
    descEl.className = 'achievement-popup-desc';
    descEl.textContent = data.description;

    contentEl.appendChild(titleEl);
    contentEl.appendChild(nameEl);
    contentEl.appendChild(descEl);

    popup.appendChild(iconEl);
    popup.appendChild(contentEl);
    container.appendChild(popup);

    // Play sound if available
    if (soundManager && typeof soundManager.play === 'function') {
        soundManager.play('success');
    }

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
        popup.classList.add('achievement-popup-out');
        setTimeout(() => popup.remove(), 500);
    }, 5000);
}

/**
 * Initializes the achievement listener on a socket
 * @param {Object} socket - Socket.io client socket
 * @param {Object} [soundManager] - Optional sound manager for playing sounds
 */
export function initAchievementListener(socket, soundManager) {
    if (!socket) return;

    socket.on('achievement:unlocked', (data) => {
        if (!data || !data.name) return;
        showAchievementPopup(data, soundManager);
    });
}
